import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StatusGlyph } from '../board/StatusGlyph';
import { cardFill } from '../design/tokens';
import { tagTones } from '../design/tagPalette';
import type { Theme } from '../design/tokens';
import { flat } from '../entity/entity';
import type { Folder } from '../entity/folders';
import type { Arc } from '../plan/arc';
import { arcHref } from '../routes/ArcRoute';
import type { CorpusItem } from '../thoughts/corpus';
import type { buildGathering } from '../thoughts/gathering';
import { groupByDay, ThoughtDays, useColumnCount, wallWidth } from '../thoughts/ThoughtWall';
import type { useThoughtWindow } from '../thoughts/useThoughtWindow';
import type { Distilled } from './distilled';
import { folderMark, markWhereDiffers, TagMarkGlyph } from './TagMark';
import type { TagMark } from './TagMark';
import { folderArcs } from './membership';
import type { FolderArcOrder, FolderCount } from './membership';
import { countsSentence } from './Counts';
import { fileHref, folderHref } from './routes';

/**
 * One folder, open: a place standing on its tag's ground.
 *
 * A head — where it sits, its name, what the app has distilled about it —
 * then the folders inside it as tiles, then either its thoughts, the Thoughts
 * page's own wall, or its arcs, as Plan's rows. Nothing is counted and
 * nothing is capped: the day grouping carries three thoughts as well as a
 * thousand. The breadcrumb and the tiles are how the tree is walked.
 */

/** Notes decrypted per step as the wall scrolls. */
const PAGE = 60;

export interface FolderPageProps {
  folder: Folder;
  /** The folder above, for the breadcrumb; null at the top. */
  parent: Folder | null;
  arcs: Arc[];
  /** Its thoughts, with the folders inside when [include] is on. */
  notes: CorpusItem[];
  include: boolean;
  onInclude: (on: boolean) => void;
  distilled: Distilled | null;
  opened: ReturnType<typeof useThoughtWindow>;
  gathering: ReturnType<typeof buildGathering>;
  fillOf: (path: string) => string;
  /** What each folder inside holds, by folder id. */
  counts?: ReadonlyMap<string, FolderCount>;
  /** A card's mark, where its tag is not this folder. */
  markOf?: (path: string) => TagMark | null;
  theme: Theme;
  /** How many notes the caller has decrypted; raising it reads more. */
  limit: number;
  onLimit: (limit: number) => void;
}

export function FolderPage(props: FolderPageProps) {
  const { folder, parent, notes, theme } = props;
  const navigate = useNavigate();
  // Thoughts first, always; not remembered, so every folder opens on them.
  const [mode, setMode] = useState<'thoughts' | 'arcs'>('thoughts');
  const [withDone, setWithDone] = useState(false);
  const [arcOrder, setArcOrder] = useState<FolderArcOrder>('updated');
  const page = useRef<HTMLDivElement>(null);
  const columns = useColumnCount(page);
  const sentinel = useRef<HTMLDivElement>(null);
  const now = useMemo(() => new Date(), [notes]);

  const shown = useMemo(
    () => notes.slice(0, props.limit).filter((item) => !props.opened.get(item.path)?.tombstone),
    [notes, props.limit, props.opened],
  );
  const days = useMemo(() => groupByDay(shown, now), [shown, now]);
  const arcs = useMemo(() => folderArcs(props.arcs, withDone, arcOrder), [props.arcs, withDone, arcOrder]);

  const { limit, onLimit } = props;
  useEffect(() => {
    const node = sentinel.current;
    if (!node || mode !== 'thoughts' || limit >= notes.length) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onLimit(limit + PAGE);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [mode, limit, notes.length, onLimit]);

  const ground = folder.hue ? { background: tagTones(theme, folder.hue).ground } : undefined;
  const empty = props.arcs.length === 0 && notes.length === 0 && folder.children.length === 0;

  return (
    <div className="fd-page" style={ground} ref={page}>
      <header className="fd-head">
        {parent && (
          <Link className="fd-crumb" to={folderHref(parent.id)}>
            <TagMarkGlyph mark={folderMark(parent, theme)} size={12} named />
            {parent.name} ›
          </Link>
        )}
        <div className="fd-head-row">
          <h1 className="fd-name">{folder.name}</h1>
          <div className="fd-head-controls">
            {mode === 'arcs' && (
              <>
                <div className="plan-switch fd-order" role="group" aria-label="Arcs in order of">
                  {(['updated', 'status'] as const).map((option) => (
                    <button key={option} type="button" aria-pressed={arcOrder === option} onClick={() => setArcOrder(option)}>
                      {option === 'updated' ? 'Updated' : 'Status'}
                    </button>
                  ))}
                </div>
                <label className="fd-check">
                  <input type="checkbox" checked={withDone} onChange={(e) => setWithDone(e.target.checked)} />
                  Done
                </label>
              </>
            )}
            <div className="plan-switch" role="group" aria-label="What this tag shows">
              {(['thoughts', 'arcs'] as const).map((option) => (
                <button key={option} type="button" aria-pressed={mode === option} onClick={() => setMode(option)}>
                  {option === 'thoughts' ? 'Thoughts' : 'Arcs'}
                </button>
              ))}
            </div>
          </div>
        </div>
        {props.distilled && <DistilledState distilled={props.distilled} />}
      </header>

      {folder.children.length > 0 && (
        <Inside
          folders={folder.children}
          around={folder}
          counts={props.counts}
          columns={columns}
          theme={theme}
          include={props.include}
          onInclude={props.onInclude}
        />
      )}

      {empty ? (
        <NeverUsed folder={folder} />
      ) : mode === 'thoughts' ? (
        <div className="fd-thoughts">
          <ThoughtDays
            days={days}
            columns={columns}
            opened={props.opened}
            gathering={props.gathering}
            fillOf={props.fillOf}
            markOf={props.markOf}
            onOpen={(item) => navigate(fileHref(item.path))}
          />
          {notes.length === 0 && <p className="fd-quiet">No thoughts here.</p>}
          <div ref={sentinel} className="th-sentinel" />
        </div>
      ) : (
        <div className="fd-arcs">
          {arcs.length === 0 && <p className="fd-quiet">No arcs here.</p>}
          {arcs.map((arc) => (
            <FolderArcRow key={arc.path} arc={arc} onOpen={() => navigate(arcHref(arc.name))} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The folders inside, as tiles to walk into, and whether their thoughts join
 * this folder's own. They keep to the page's measure, [columns] cards wide.
 *
 * A notebook is drawn as a folder: its tag's band, a small empty tab on its
 * shoulder, and on its front its icon leading its name — nothing else, since
 * it is there to be recognised and walked into, not read. Its shape already
 * says folder, so a notebook with no icon, or one whose icon this folder
 * already shows, has its name lead alone. Any other folder is a small card in
 * the hue it inherits, led by the folder glyph in that hue, with what it
 * holds under its name.
 */
export function Inside({
  folders,
  around = null,
  counts,
  columns,
  theme,
  include,
  onInclude,
}: {
  folders: readonly Folder[];
  /** The folder they are inside, whose own mark a notebook leaves off. */
  around?: Folder | null;
  /** What each holds, by folder id. */
  counts?: ReadonlyMap<string, FolderCount>;
  columns: number;
  theme: Theme;
  include?: boolean;
  /** Absent where there is nothing to include, as on the page of every folder. */
  onInclude?: (on: boolean) => void;
}) {
  const notebooks = folders.filter((f) => f.notebook);
  const plain = folders.filter((f) => !f.notebook);
  const own = around ? folderMark(around, theme) : null;
  return (
    <section className="fd-inside" style={{ '--wallWidth': `${wallWidth(columns)}px` } as CSSProperties}>
      {onInclude && (
        <div className="fd-inside-head">
          <span className="fd-label">Inside</span>
          <label className="fd-check">
            <input type="checkbox" checked={include} onChange={(e) => onInclude(e.target.checked)} />
            Include their thoughts
          </label>
        </div>
      )}
      {notebooks.length > 0 && (
        <div className="fd-tiles is-notebooks">
          {notebooks.map((f) => {
            const mark = markWhereDiffers(folderMark(f, theme), own);
            return (
              <Link
                key={f.id}
                className="fd-tile is-notebook"
                to={folderHref(f.id)}
                style={f.hue ? { background: tagTones(theme, f.hue).band } : undefined}
              >
                <span className="fd-tile-tab" aria-hidden="true" />
                <span className="fd-tile-front">
                  {mark?.icon && (
                    <span className="fd-tile-mark">
                      <TagMarkGlyph mark={mark} size={24} named />
                    </span>
                  )}
                  <span className="fd-tile-name">{f.name}</span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
      {plain.length > 0 && (
        <div className="fd-tiles">
          {plain.map((f) => {
            const holds = counts?.get(f.id);
            const said = holds ? countsSentence(holds.held) : '';
            return (
              <Link key={f.id} className="fd-tile" to={folderHref(f.id)} style={{ background: cardFill(theme, f.hue) }}>
                <span className="fd-tile-line">
                  <TagMarkGlyph mark={{ ...folderMark(f, theme), icon: null }} size={14} named />
                  <span className="fd-tile-name t-entityTitle">{f.name}</span>
                </span>
                {said && <span className="fd-tile-holds">{said}</span>}
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** An arc as Plan's Recent draws it: its state and its name, and what it is
 *  waiting on you for when it is. A parked one reads quieter. */
function FolderArcRow({ arc, onOpen }: { arc: Arc; onOpen: () => void }) {
  const needs = flat(arc.frontMatter['needs']).trim();
  return (
    <button type="button" className={`plan-row fd-arc${arc.status === 'parked' ? ' is-parked' : ''}`} onClick={onOpen}>
      <span className="plan-glyph status-button">
        <StatusGlyph status={arc.status} size={10} />
      </span>
      <span className="plan-text">
        <span className="plan-row-name">{arc.title}</span>
        {arc.status === 'needs-input' && (
          <span className="plan-row-line is-asking">waiting on you{needs ? ` — ${needs}` : ''}</span>
        )}
      </span>
    </button>
  );
}

/** What the app has distilled about this folder: the state, and its open
 *  threads and what is settled one quiet line away. */
function DistilledState({ distilled }: { distilled: Distilled }) {
  const [open, setOpen] = useState(false);
  const more = distilled.open.length > 0 || distilled.settled.length > 0;
  return (
    <div className="fd-distilled">
      <p className="fd-state">{distilled.state}</p>
      <div className="fd-distilled-foot">
        {more && (
          <button type="button" className="fd-quiet-button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            Open threads and what is settled {open ? '⌃' : '⌄'}
          </button>
        )}
        {distilled.date && <span className="fd-distilled-date">distilled {distilled.date}</span>}
      </div>
      {open && (
        <div className="fd-distilled-lists">
          {distilled.open.length > 0 && (
            <div>
              <span className="fd-label">Open threads</span>
              <ul>
                {distilled.open.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>
          )}
          {distilled.settled.length > 0 && (
            <div>
              <span className="fd-label">Settled</span>
              <ul>
                {distilled.settled.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Empty is not one state.
 *
 * A slot waiting to be filled and a mistake look identical unless the page
 * says who made it and when anything last landed — and 14 of the 33 folders
 * were minted by an assistant run, so this is common rather than exotic.
 */
function NeverUsed({ folder }: { folder: Folder }) {
  return (
    <section className="fd-empty">
      <h2 className="fd-empty-title">Nothing has ever been placed here.</h2>
      <p className="t-bodyProse fd-empty-note">
        {folder.notebook ? 'A notebook' : 'A tag'} with no members is either a slot
        waiting to be filled or a mistake, and this page cannot yet tell you which — the vault does
        not record who made a tag or when anything last landed in it.
      </p>
      <p className="t-metaMono fd-empty-hint">
        Placing from the browser is not built yet. The phone and the assistant can put something
        here.
      </p>
    </section>
  );
}
